import { saveAs } from 'file-saver';
import * as Storage from './storage';
import { get, patch, post } from './request';

export interface ConsolidatedExportResult {
  processed: number;
  skipped: number;
}

export const getYelumSpreadsheetUrl = (batchId: string): string =>
  `${process.env.REACT_APP_API_URL}/fire-insurance-batches/${batchId}/yelum-spreadsheet`;

export const downloadYelumSpreadsheet = async (batchId: string): Promise<void> => {
  const response = await fetch(getYelumSpreadsheetUrl(batchId), {
    headers: {
      Authorization: Storage.get('id_token') || '',
    },
  });

  if (!response.ok) {
    let message = 'Erro ao baixar planilha do lote';
    try {
      const body = await response.json();
      message = body?.message || body?.error || message;
    } catch {
      // resposta não JSON
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const filenameMatch = disposition?.match(/filename="([^"]+)"/);
  const filename = filenameMatch?.[1] || `lote_yelum_${batchId}.xlsx`;

  saveAs(blob, filename);
};

export const setBatchManualProcessing = async (
  batchId: string,
  isManualProcessing: boolean,
): Promise<void> => {
  await patch(`/fire-insurance-batches/${batchId}`, { isManualProcessing });
};

export interface UpdateBatchCodesPayload {
  quotationCode: string;
  proposalCode?: string | null;
  policyNumber?: string | null;
  status: string;
}

export interface UpdatedBatchCodesResponse {
  id: string;
  quotationCode: string;
  proposalCode: string | null;
  policyNumber: string | null;
  status: string;
  nextTransmissionDate?: string | Date | null;
  lastTransmittedAt?: string | Date | null;
  transmissionDate?: string | Date | null;
  dueDate?: number | null;
  quoteSyncQueued?: boolean;
}

export const updateBatchCodes = async (
  batchId: string,
  payload: UpdateBatchCodesPayload,
): Promise<UpdatedBatchCodesResponse> => {
  return patch(`/fire-insurance-batches/${batchId}`, payload);
};

export const markBatchTransmitted = async (
  batchId: string,
): Promise<UpdatedBatchCodesResponse> => {
  return post(`/fire-insurance-batches/${batchId}/mark-transmitted`, {});
};

export interface TransmitBatchResponse {
  batchId: string;
  queued: boolean;
}

export const transmitBatch = async (
  batchId: string,
  force = false,
): Promise<TransmitBatchResponse> => {
  return post(`/fire-insurance-batches/${batchId}/transmit`, { force });
};

export interface SyncBatchNumberingResponse {
  batchId: string;
  queued: boolean;
}

export const syncBatchNumbering = async (
  batchId: string,
): Promise<SyncBatchNumberingResponse> => {
  return post(`/fire-insurance-batches/${batchId}/sync-numbering`, {});
};

export interface TransferBatchPoliciesPayload {
  targetBatchId: string;
  policyIds: string[];
  recreateMissingQuotes?: boolean;
}

export interface TransferPreviewItem {
  policyId: string;
  tenantName: string;
  tenantDocument: string;
  status: 'matched' | 'not_found' | 'ambiguous' | 'collision';
  matchedItemNo?: number;
  previousItemNo?: string;
  proposedExternalPolicyCode?: string;
  message?: string;
}

export interface TransferTargetBatch {
  id: string;
  batchNumber: number;
  quotationCode: string;
  proposalCode?: string;
  status: string;
  estate?: { name: string };
}

export interface TransferBatchPoliciesResponse {
  transferId: string;
  status: string;
}

export type TransferStatusItemStatus =
  | 'pending'
  | 'matched'
  | 'not_found'
  | 'ambiguous'
  | 'persisting'
  | 'recreating'
  | 'remote_created'
  | 'certificate_error'
  | 'budget_error'
  | 'document_error'
  | 'done'
  | 'failed';

export interface TransferStatusItem {
  policyId: string;
  tenantName: string;
  tenantDocument: string;
  status: TransferStatusItemStatus | string;
  step?: string;
  matchedItemNo?: number;
  previousItemNo?: string;
  recreated?: boolean;
  error?: string;
}

export interface TransferStatusResponse {
  id: string;
  sourceBatchId: string;
  targetBatchId: string;
  status: string;
  items: TransferStatusItem[];
  hasCertificatePending: boolean;
  sourceBatchClosed: boolean;
  errorMessage?: string | null;
}

export const getBatchTransferTargets = async (
  batchId: string,
): Promise<TransferTargetBatch[]> => {
  return get(`/fire-insurance-batches/${batchId}/transfer-targets`);
};

export const previewTransferPolicies = async (
  sourceBatchId: string,
  payload: TransferBatchPoliciesPayload,
): Promise<TransferPreviewItem[]> => {
  return post(
    `/fire-insurance-batches/${sourceBatchId}/transfer-policies/preview`,
    payload,
  );
};

export const transferBatchPolicies = async (
  sourceBatchId: string,
  payload: TransferBatchPoliciesPayload,
): Promise<TransferBatchPoliciesResponse> => {
  return post(
    `/fire-insurance-batches/${sourceBatchId}/transfer-policies`,
    payload,
  );
};

export const getBatchTransferStatus = async (
  transferId: string,
): Promise<TransferStatusResponse> => {
  return get(`/fire-insurance-batches/transfers/${transferId}`);
};

export interface RestartBatchPayload {
  competenceMonth: string;
}

export interface RestartPreviewItem {
  policyId: string;
  tenantName: string;
  tenantDocument: string;
  oldExternalPolicyCode?: string | null;
  status: string;
}

export interface RestartPreviewResponse {
  itemCount: number;
  items: RestartPreviewItem[];
  oldQuotationCode?: string | null;
  oldProposalCode?: string | null;
  oldPolicyNumber?: string | null;
}

export interface RestartBatchResponse {
  restartId: string;
  status: string;
}

export type RestartStatusItemStatus =
  | 'pending'
  | 'creating_quote'
  | 'adding'
  | 'added'
  | 'failed';

export interface RestartStatusItem {
  policyId: string;
  tenantName: string;
  tenantDocument: string;
  oldExternalPolicyCode?: string | null;
  newItemNo?: number;
  newExternalPolicyCode?: string;
  status: RestartStatusItemStatus | string;
  error?: string;
  certificateRemoved?: boolean;
}

export interface RestartStatusResponse {
  id: string;
  batchId: string;
  status: string;
  competenceMonth: string;
  oldQuotationCode?: string | null;
  oldProposalCode?: string | null;
  oldPolicyNumber?: string | null;
  newQuotationCode?: string | null;
  items: RestartStatusItem[];
  errorMessage?: string | null;
}

export const previewRestartBatch = async (
  batchId: string,
): Promise<RestartPreviewResponse> => {
  return post(`/fire-insurance-batches/${batchId}/restart/preview`, {});
};

export const restartBatch = async (
  batchId: string,
  payload: RestartBatchPayload,
): Promise<RestartBatchResponse> => {
  return post(`/fire-insurance-batches/${batchId}/restart`, payload);
};

export const getBatchRestartStatus = async (
  restartId: string,
): Promise<RestartStatusResponse> => {
  return get(`/fire-insurance-batches/restarts/${restartId}`);
};

export const downloadYelumConsolidatedSpreadsheet = async (
  batchIds: string[],
): Promise<ConsolidatedExportResult> => {
  const url = `${process.env.REACT_APP_API_URL}/fire-insurance-batches/yelum-spreadsheet/consolidated`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: Storage.get('id_token') || '',
    },
    body: JSON.stringify({ batchIds }),
  });

  if (!response.ok) {
    let message = 'Erro ao gerar planilha consolidada';
    try {
      const body = await response.json();
      message = body?.message || body?.error || message;
    } catch {
      // resposta não JSON
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const filenameMatch = disposition?.match(/filename="([^"]+)"/);
  const filename = filenameMatch?.[1] || 'lotes_yelum_consolidado.xlsx';

  saveAs(blob, filename);

  const processed = parseInt(response.headers.get('X-Batches-Processed') || '0', 10);
  const skipped = parseInt(response.headers.get('X-Batches-Skipped') || '0', 10);

  return { processed, skipped };
};

export type BatchEventType =
  | 'BATCH_STATUS_CHANGED'
  | 'TRANSMISSION_REQUESTED'
  | 'BATCH_EDITED'
  | 'QUOTE_SYNC'
  | 'TRANSMISSION_BLOCKED'
  | 'PROPOSAL_CREATED'
  | 'QUOTE_ROLLED'
  | 'TRANSMISSION_REGISTERED'
  | 'COMPETENCE_CLOSED_WITHOUT_ITEMS'
  | 'TRANSMISSION_FAILED'
  | 'POST_TRANSMISSION_CHECK'
  | 'QUOTE_ITEM_OVERWRITTEN';

export interface BatchEvent {
  id: string;
  batchId: string;
  eventType: BatchEventType;
  summary: string;
  payload?: Record<string, any> | null;
  quotationCode?: string | null;
  actorUserId?: string | null;
  actorLabel?: string | null;
  createdAt: string;
}

export const getBatchEvents = async (batchId: string): Promise<BatchEvent[]> => {
  return get(`/fire-insurance-batches/${batchId}/events`);
};
